import { useCallback, useEffect, useState } from "react";

import { useStartAdtLogon } from "@/hooks/useApi";
import { detectAdtExtension } from "@/lib/adt-extension";
import { errorDetail } from "@/lib/api-error";
import { callbackMode } from "@/lib/code-companion-session";

/** Quando re-sondar a extensão. Ver a nota de política abaixo. */
export type AdtExtensionWatch = "once" | "on-focus";

/**
 * O fluxo de logon ADT: pedir o reentrance, abrir a aba do SSO, e o que cerca isso.
 *
 * ⚠️ **Existe porque o par estava copiado em quatro telas** (#593), e a cópia mais nova
 * era a com menos guarda. Dois defeitos vinham junto em cada uma:
 *
 * 1. **Erro engolido.** `mutateAsync` que rejeita dentro de um `onClick` sem `await`
 *    produz uma promise rejeitada que ninguém observa, e o botão morre sem nada na tela.
 *    Por isso `start` **não devolve promise**: não há o que ficar sem `await`.
 * 2. **Host publicado sem a extensão.** O reentrance volta por `localhost`, e sem a
 *    adt-connect o ticket morre no `SADT_RESOURCE 034` (S-070). Duas telas avisavam,
 *    duas não.
 *
 * 🔶 **As duas políticas de sondagem são parâmetro, não decisão do hook.** A faixa sonda
 * uma vez, porque não é tela de onboarding; o card re-sonda no foco, porque instalar É
 * sair para outra aba e voltar, e sem isso o aviso continuaria dizendo "ausente" logo
 * depois de a pessoa ter feito o que pedimos. A diferença é deliberada, e antes disto era
 * defendida só por um comentário: uniformizá-la por acidente era questão de tempo.
 *
 * 🔶 **`enabled` é de quem chama.** "O logon está sendo oferecido agora" depende de dado
 * que o hook não tem (urgência da sessão na faixa, sessão desconectada no card, pergunta
 * aberta com janela apertada no RunPanel). Sem esse corte, quem tem 25 minutos de sessão
 * e um navegador onde a sonda não responde veria "instale a extensão" para sempre ao lado
 * de uma sessão viva.
 */
export function useAdtLogonFlow(
  projectId: string,
  { enabled, watch = "once" }: { enabled: boolean; watch?: AdtExtensionWatch },
) {
  const logon = useStartAdtLogon(projectId);
  const [error, setError] = useState<string | null>(null);
  // `undefined` = ainda procurando · `null` = ausente · objeto = instalada.
  //
  // Tri-estado, e não booleano: o card mostra a versão quando acha, e um booleano
  // colapsaria "ainda procurando" com "ausente", que é o que faz alguém ler "instale a
  // extensão" tendo a extensão instalada.
  const [extension, setExtension] = useState<{ version: string } | null | undefined>();

  const needsExtension = callbackMode(window.location.hostname) === "localhost";
  const found = !!extension;

  useEffect(() => {
    if (!needsExtension || !enabled || found) return;
    let alive = true;
    const probe = () =>
      detectAdtExtension().then((r) => {
        if (alive) setExtension(r ? { version: r.version } : null);
      });
    probe();
    if (watch !== "on-focus") return () => void (alive = false);
    window.addEventListener("focus", probe);
    return () => {
      alive = false;
      window.removeEventListener("focus", probe);
    };
  }, [needsExtension, enabled, found, watch]);

  const start = useCallback(() => {
    // Reseta antes de tentar: sem isto a mensagem de uma recusa anterior sobreviveria a
    // uma tentativa bem-sucedida.
    setError(null);
    void (async () => {
      try {
        const { reentrance_url } = await logon.mutateAsync();
        window.open(reentrance_url, "_blank", "noopener");
      } catch (e) {
        // `errorDetail` devolve "" quando houve erro sem texto, e quem renderiza põe a
        // frase traduzida no lugar. `null` aqui apagaria a caixa.
        setError(errorDetail(e) ?? "");
      }
    })();
  }, [logon]);

  return { start, isPending: logon.isPending, error, needsExtension, extension };
}
